import { useGrowthBookFeature } from '../context/growthbook-context'

/**
 * Higher Order Component that provides Growthbook feature flag functionality
 * @param WrappedComponent - Component to wrap
 * @param features - Single feature name or array of feature names to check
 * @returns Wrapped component with isGrowthbookFeatureOn and featureStatus props, or null if features are still loading
 */
export const withGrowthbookFeature = (WrappedComponent, features) => {
  return (props) => {
    const featureNames = Array.isArray(features) ? features : [features]

    const toCamelCase = (str) => {
      return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase())
    }

    const primaryFeature = useGrowthBookFeature(featureNames[0])

    const additionalFeaturesData = featureNames.slice(1).map((featureName) => ({
      name: featureName,
      data: useGrowthBookFeature(featureName),
    }))

    const isAnyFeatureLoading =
      primaryFeature.isLoading ||
      additionalFeaturesData.some((feature) => feature.data.isLoading)

    if (isAnyFeatureLoading) return null

    const additionalFeatures = additionalFeaturesData.reduce(
      (acc, { name, data }) => {
        const camelCaseName = toCamelCase(name)
        const capitalizedName = `${camelCaseName
          .charAt(0)
          .toUpperCase()}${camelCaseName.slice(1)}`
        return {
          ...acc,
          [`isGrowthbookFeatureOn${capitalizedName}`]: data.isEnabled,
          [`featureStatus${capitalizedName}`]: data,
        }
      },
      {},
    )

    return (
      <WrappedComponent
        {...props}
        isGrowthbookFeatureOn={primaryFeature.isEnabled}
        featureStatus={primaryFeature}
        {...additionalFeatures}
      />
    )
  }
}
